// /rankings/:slug — someone's shared Custom Ranking. Public, no account
// needed. The point of the page is the next step: "Make Your Own", which
// starts a fresh ranking with the same title and size (never the titles).
//
// The server renders this page's <head> (title, Open Graph card, noindex) for
// link previews; this component also sets it for in-app navigation. It shows
// the ranking's PUBLISHED version only (never the owner's unpublished edits);
// a draft or unpublished ranking shows only that it is private — no title,
// owner or titles.
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { EmptyShareState, PublicShareLayout } from '../../components/PublicShare/PublicShareLayout';
import TopMoviesGrid from '../../components/TopMovies/TopMoviesGrid';
import RankingSharePanel from '../../components/Rankings/RankingSharePanel';
import { RankingService } from '../../services/rankingService';
import { useAuth } from '../../context/AuthContext';
import { useShareMetadata } from '../../utils/shareMetadata';
import { setReferral } from '../../attribution/referral';
import { ANALYTICS_EVENTS } from '../../analytics';
import { trackRanking } from '../../analytics/rankingAnalytics';
import TmdbAttribution from '../../components/TmdbAttribution/TmdbAttribution';
import styles from '../PublicTopMovies/PublicTopMovies.module.css';

const COMPOSITION_LABEL = { movie: 'Movies', tv: 'TV shows', mixed: 'Movies & TV' };

export function SharedRankingView({ payload, viewer }) {
    const { ranking, owner, items, slug } = payload;
    const ownerName = owner.displayName || `@${owner.username}`;
    const isOwner = Boolean(viewer?.username) && viewer.username === owner.username;
    const [showShare, setShowShare] = useState(false);
    const makeYourOwn = `/rankings/new?from=${encodeURIComponent(slug)}`;

    return (
        <PublicShareLayout>
            <header className={styles.hero}>
                <span className={styles.eyebrow}>Top {ranking.size} · {COMPOSITION_LABEL[ranking.composition] || 'Ranking'}</span>
                <h1 style={{ overflowWrap: 'anywhere' }}>{ranking.title}</h1>
                <p className={styles.sub}>
                    Created by <Link to={`/u/${encodeURIComponent(owner.username)}`}>{ownerName}</Link>
                    {owner.displayName ? <> (@{owner.username})</> : null}
                </p>
                {ranking.description && <p className={styles.sub} style={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{ranking.description}</p>}
            </header>

            {/* Numbers follow the owner's choice; the list stays in their order either way. */}
            <TopMoviesGrid movies={items} ranked={ranking.showPositions !== false} label={`${ranking.title}, in order`} size={ranking.size} />

            <div className={styles.actions}>
                {isOwner ? (
                    <>
                        <Link to="/me/rankings" className={styles.primary}>Manage my rankings</Link>
                        <button type="button" className={styles.secondary} onClick={() => setShowShare((v) => !v)}>{showShare ? 'Hide sharing' : 'Share'}</button>
                    </>
                ) : (
                    <button type="button" className={styles.secondary} onClick={() => setShowShare((v) => !v)}>{showShare ? 'Hide sharing' : 'Share this ranking'}</button>
                )}
            </div>

            {showShare && (
                <section className={styles.sharePanel} aria-label="Share this ranking">
                    <RankingSharePanel
                        items={items}
                        size={ranking.size}
                        composition={ranking.composition}
                        showPositions={ranking.showPositions !== false}
                        shared={{ slug, shareUrl: payload.shareUrl, title: ranking.title, ownerUsername: owner.username || null }}
                        isOwner={isOwner}
                        loggedIn={Boolean(viewer)}
                        surface={isOwner ? 'public_page_owner' : 'public_page'}
                    />
                </section>
            )}

            <section className={styles.cta}>
                <div>
                    <p className={styles.ctaKicker}>Your turn</p>
                    <h2>Think you can make a better list?</h2>
                    <p>Start your own “{ranking.title}” with the same Top {ranking.size} — pick your own titles and share it. Free, no account needed to start.</p>
                </div>
                <Link
                    to={makeYourOwn}
                    className={styles.primary}
                    onClick={() => trackRanking(ANALYTICS_EVENTS.RANKING_MAKE_YOUR_OWN_CLICKED, {
                        size: ranking.size, composition: ranking.composition || 'none', logged_in: Boolean(viewer), is_owner: isOwner,
                    }, { listSlug: slug })}
                >
                    Make Your Own
                </Link>
            </section>
            <TmdbAttribution variant="compact" className={styles.attribution} />
        </PublicShareLayout>
    );
}

export default function PublicRanking() {
    const { slug } = useParams();
    const { user } = useAuth();
    const [payload, setPayload] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        setPayload(null);
        setError(null);
        RankingService.getShared(slug)
            .then((data) => {
                if (!mounted) return;
                setPayload(data);
                if (!data.isPrivate) {
                    const isOwner = Boolean(user?.username) && user.username === data.owner?.username;
                    // A visitor who later signs up is attributed to this ranking.
                    if (!isOwner && !user) setReferral('ranking', data.slug);
                    trackRanking(
                        ANALYTICS_EVENTS.RANKING_VIEWED,
                        { size: data.ranking.size, composition: data.ranking.composition || 'none', logged_in: Boolean(user), is_owner: isOwner },
                        { onceKey: data.slug, listSlug: data.slug },
                    );
                }
            })
            .catch((err) => mounted && setError(err?.statusCode === 404 ? 'not_found' : (err.message || 'error')));
        return () => { mounted = false; };
    }, [slug, user]);

    const metadata = useMemo(() => {
        if (!payload || payload.isPrivate) return { title: 'Ranking | Aluvo', description: 'A ranking on Aluvo.', url: `${window.location.origin}/rankings/${slug}` };
        const ownerName = payload.owner.displayName || `@${payload.owner.username}`;
        return {
            title: `${payload.ranking.title} — ${ownerName} | Aluvo`,
            description: `${ownerName}'s Top ${payload.ranking.size}: ${payload.items.slice(0, 3).map((m) => m.title).join(', ')}… Think you can make a better list?`,
            url: payload.shareUrl,
            image: payload.cardUrl,
            type: 'article',
        };
    }, [payload, slug]);
    useShareMetadata(metadata);

    if (error === 'not_found') return <EmptyShareState icon="🏆" title="Ranking not found" message="This ranking does not exist or is no longer shared." />;
    if (error) return <EmptyShareState icon="⚠️" title="Couldn't load this ranking" message="Please check your connection and try again." />;
    if (!payload) return <EmptyShareState icon="◎" title="Loading ranking" message="Rolling the opening credits…" />;
    if (payload.isPrivate) return <EmptyShareState icon="🔒" title="This ranking is private." message="The owner has turned off sharing for this ranking." />;
    return <SharedRankingView payload={payload} viewer={user} />;
}
